// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TextField from "@steiner/MobiusOverrides/TextField/TextField";
import { placeHolders } from "@steiner/XDL/constants";
import { ContactInformationFields, LoginInformationFields } from "@steiner/XDL/types";
import { useValidate } from "@steiner/XDL/Utilities";
import { FormikProps } from "formik";
import React, { forwardRef, Ref, useImperativeHandle } from "react";

type Props = {
    onSubmit: (values: ContactInformationFields) => void;
    onBackClick: () => void;
};

export interface ContactInfoFormStyles {
    container?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
}

export const contactInformationFormStyles: ContactInfoFormStyles = {};

const styles = contactInformationFormStyles;

const ContactInformationForm = ({ onSubmit, onBackClick }: Props, ref: Ref<FormikProps<LoginInformationFields>>) => {
    const basicInfoValidators = {
        firstName: `required|firstName|max:75`,
        lastName: `required|lastName|max:75`,
        jobTitle: "required|max:25",
        phone: `required|phone`,
        companyName: `required|companyName|max:75`,
    };

    const { field, handleSubmit, formik, isSubmitting } = useValidate({
        initialValues: {
            firstName: "",
            lastName: "",
            jobTitle: "",
            phone: "",
            companyName: "",
        },
        validators: basicInfoValidators,
        onSubmit: (fieldValues: ContactInformationFields, { setSubmitting }: any) => {
            setSubmitting(false);
            submitHandler(fieldValues);
        },
    });

    const submitHandler = (fieldValues: ContactInformationFields) => {
        onSubmit(fieldValues);
    };

    useImperativeHandle(ref, () => ({
        ...formik,
    }));

    return (
        <LoadingOverlay loading={isSubmitting}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...field("firstName")}
                        label={translate("First Name")}
                        placeholder={translate("First Name")}
                        error={field("firstName").error && "First name is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...field("lastName")}
                        label={translate("Last Name")}
                        placeholder={translate("Last Name")}
                        error={field("lastName").error && "Last name is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...field("jobTitle")}
                        label={translate("Job Title")}
                        placeholder={translate("Job Title")}
                        error={field("jobTitle").error && "Job title is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...field("companyName")}
                        label={translate("Company Name")}
                        placeholder={translate("Company Name")}
                        error={field("companyName").error && "Company name is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...field("phone")}
                        label={translate("Phone Number")}
                        placeholder={placeHolders.phone}
                        error={field("phone").error && "Phone number is a required field"}
                    />
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.buttonsWrapper} className="form-buttons">
                <Button onClick={onBackClick} variant="tertiary">
                    {translate("Previous")}
                </Button>
                <Button onClick={handleSubmit as any} className="stepperNextButton-2">
                    {translate("Next")}
                </Button>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default forwardRef(ContactInformationForm);
